import React, { useState, useCallback, useMemo } from "react";
import { View } from "react-native";
import { useQueryClient } from "@tanstack/react-query";
import { AntDesign } from "@expo/vector-icons";
import { extractApiError } from "@/lib/api/errors";
import { Modal } from "@/components/ui/Modal";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { AppTextInput } from "@/components/ui/TextInput";
import { Icon } from "@/components/ui/Icon";
import { SelectField } from "@/components/ui/SelectField";
import { useI18n } from "@/context/I18nContext";
import { invitationsApi } from "@/lib/api/invitations";
import type { SupportedLocale } from "@chinese-vendor/i18n";
import { SuccessState } from "@/components/ui/SuccessState";
import { ErrorState } from "@/components/ui/ErrorState";

type State = "form" | "success" | "error";

export interface SendInvitationModalProps {
  visible: boolean;
  onDismiss: () => void;
  /** Called after a successful send so the list can refresh. */
  onSent?: () => void;
}

export function SendInvitationModal({ visible, onDismiss, onSent }: SendInvitationModalProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [state, setState] = useState<State>("form");
  const [email, setEmail] = useState("");
  const [locale, setLocale] = useState<SupportedLocale>("en");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const localeOptions = useMemo(
    () => [
      { label: t("common.language.en"), value: "en" },
      { label: t("common.language.zh"), value: "zh-CN" },
      { label: t("common.language.ar"), value: "ar" },
    ],
    [t]
  );

  const resetForm = useCallback(() => {
    setState("form");
    setEmail("");
    setLocale("en");
    setSubmitting(false);
    setError(null);
  }, []);

  const handleDismiss = useCallback(() => {
    resetForm();
    onDismiss();
  }, [resetForm, onDismiss]);

  const handleSend = useCallback(async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      setError(t("auth.login.error.invalidEmail"));
      setState("error");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await invitationsApi.send({ email: trimmedEmail, locale });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["invitations", "list"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "stats"] }),
      ]);
      setState("success");
      onSent?.();
    } catch (err) {
      setError(extractApiError(err, t("common.error.generic")));
      setState("error");
    } finally {
      setSubmitting(false);
    }
  }, [email, locale, queryClient, t, onSent]);

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      closeOnBackdrop={!submitting}
      footer={
        state === "form" ? (
          <View className="flex-row gap-4 justify-end">
            <Button
              label="staff.invitations.send.cancel"
              onPress={handleDismiss}
              variant="tertiary"
              disabled={submitting}
            />
            <Button
              label="staff.invitations.send.sendInvite"
              onPress={handleSend}
              variant="primary"
              loading={submitting}
            />
          </View>
        ) : null
      }
    >
      {state === "form" && (
        <View className="gap-6 items-start w-full">
          {/* Icon + title + subtitle */}
          <View className="gap-6 items-start w-full">
            <View className="self-start bg-blue-10 rounded-full p-5">
              <Icon as={AntDesign} name="user-add" size={40} className="text-blue-50" />
            </View>
            <View className="gap-2 items-start w-full">
              <AppText
                weight="semibold"
                className="text-xl leading-tight tracking-tight text-dark-text-50"
              >
                {t("staff.invitations.send.inviteVendorTitle")}
              </AppText>
              <AppText className="text-base text-supporting-text">
                {t("staff.invitations.send.inviteVendorSubtitle")}
              </AppText>
            </View>
          </View>

          {/* Form fields */}
          <View className="gap-4 items-start w-full">
            <AppTextInput
              label="staff.invitations.send.emailLabel"
              placeholder="staff.invitations.send.emailPlaceholder"
              value={email}
              onChangeText={(v) => {
                setEmail(v);
                setError(null);
              }}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              required
            />
            <SelectField
              label="staff.invitations.send.languageLabel"
              placeholder="staff.invitations.send.languagePlaceholder"
              options={localeOptions}
              value={locale}
              onChange={(v) => setLocale(v as SupportedLocale)}
              required
            />
          </View>
        </View>
      )}

      {state === "success" && (
        <SuccessState
          title="staff.invitations.send.success"
          description="staff.invitations.send.successBody"
          className="px-0 pt-0"
        />
      )}

      {state === "error" && (
        <ErrorState
          descriptionText={error || undefined}
          description="common.error.generic"
          className="px-0 pt-0"
          onRetry={() => setState("form")}
        />
      )}
    </Modal>
  );
}
